import React, { createContext, useContext, useEffect, useState } from 'react';
import { Download, Wifi, WifiOff, RefreshCw, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';

interface PWAContextType {
  isInstallable: boolean;
  isInstalled: boolean;
  isOnline: boolean;
  updateAvailable: boolean;
  installApp: () => Promise<void>;
  updateApp: () => Promise<void>;
  showInstallPrompt: boolean;
  dismissInstallPrompt: () => void;
  enableNotifications: () => Promise<boolean>;
  notificationsEnabled: boolean;
  sendNotification: (title: string, options?: NotificationOptions) => void;
  addToHomeScreen: () => void;
}

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

const PWAContext = createContext<PWAContextType | undefined>(undefined);

export const usePWA = () => {
  const context = useContext(PWAContext);
  if (!context) {
    throw new Error('usePWA must be used within a PWAProvider');
  }
  return context;
};

interface Props {
  children: React.ReactNode;
}

export const PWAProvider: React.FC<Props> = ({ children }) => {
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Register service worker
    registerServiceWorker();

    // Check if already installed
    checkIfInstalled();

    // Listen for install prompt
    setupInstallPrompt();

    // Listen for online/offline changes
    setupNetworkListeners();

    // Check notification permission
    checkNotificationPermission();

    // Listen for app updates
    setupUpdateListener();
  }, []);

  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        setRegistration(reg);

        console.log('Service Worker registered successfully');

        // Listen for updates
        reg.addEventListener('updatefound', () => {
          const newWorker = reg.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                setUpdateAvailable(true);
              }
            });
          }
        });
      } catch (error) {
        console.error('Service Worker registration failed:', error);
      }
    }
  };

  const checkIfInstalled = () => {
    // Check if running as PWA
    const isPWA =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes('android-app://');
    setIsInstalled(isPWA);
  };

  const setupInstallPrompt = () => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      const installEvent = e as BeforeInstallPromptEvent;
      setDeferredPrompt(installEvent);
      setIsInstallable(true);

      // Show install prompt after a delay (don't be intrusive)
      setTimeout(() => {
        if (!isInstalled) {
          setShowInstallPrompt(true);
        }
      }, 10000); // Show after 10 seconds
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  };

  const setupNetworkListeners = () => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  };

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const setupUpdateListener = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data && event.data.type === 'UPDATE_AVAILABLE') {
          setUpdateAvailable(true);
        }
      });
    }
  };

  const installApp = async () => {
    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const choiceResult = await deferredPrompt.userChoice;

        if (choiceResult.outcome === 'accepted') {
          setIsInstalled(true);
          setShowInstallPrompt(false);
        }

        setDeferredPrompt(null);
        setIsInstallable(false);
      } catch (error) {
        console.error('Installation failed:', error);
      }
    }
  };

  const updateApp = async () => {
    if (registration && registration.waiting) {
      // Send message to waiting SW to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });

      // Reload the page to use the new service worker
      window.location.reload();
    }
  };

  const dismissInstallPrompt = () => {
    setShowInstallPrompt(false);
    // Don't show again for this session
    sessionStorage.setItem('install-prompt-dismissed', 'true');
  };

  const enableNotifications = async (): Promise<boolean> => {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      const enabled = permission === 'granted';
      setNotificationsEnabled(enabled);
      return enabled;
    } catch (error) {
      console.error('Failed to enable notifications:', error);
      return false;
    }
  };

  const sendNotification = (title: string, options: NotificationOptions = {}) => {
    if (!notificationsEnabled) return;

    const defaultOptions: NotificationOptions = {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: 'pwa-notification',
      requireInteraction: false,
      ...options,
    };

    if ('serviceWorker' in navigator && registration) {
      // Send via service worker for better reliability
      registration.showNotification(title, defaultOptions);
    } else {
      // Fallback to direct notification
      new Notification(title, defaultOptions);
    }
  };

  const addToHomeScreen = () => {
    if (deferredPrompt) {
      installApp();
    } else {
      // Show instructions for manual installation
      alert(
        'To install this app:\n\n1. Open browser menu\n2. Select "Add to Home Screen" or "Install App"\n3. Follow the prompts'
      );
    }
  };

  const contextValue: PWAContextType = {
    isInstallable,
    isInstalled,
    isOnline,
    updateAvailable,
    installApp,
    updateApp,
    showInstallPrompt,
    dismissInstallPrompt,
    enableNotifications,
    notificationsEnabled,
    sendNotification,
    addToHomeScreen,
  };

  return (
    <PWAContext.Provider value={contextValue}>
      {children}
      <PWAPrompts />
    </PWAContext.Provider>
  );
};

// PWA Prompts Component
const PWAPrompts: React.FC = () => {
  const {
    showInstallPrompt,
    dismissInstallPrompt,
    installApp,
    isOnline,
    updateAvailable,
    updateApp,
  } = usePWA();

  return (
    <>
      {/* Install Prompt */}
      <AnimatePresence>
        {showInstallPrompt && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:w-96"
          >
            <div className="bg-white rounded-lg shadow-xl border border-stone-200 p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Download className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-stone-900">Install App</h3>
                  <p className="text-sm text-stone-600 mt-1">
                    Install Platform Engineering Nexus for a better experience
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      size="sm"
                      onClick={installApp}
                      className="bg-primary hover:bg-primary/90"
                    >
                      Install
                    </Button>
                    <Button size="sm" variant="ghost" onClick={dismissInstallPrompt}>
                      Not now
                    </Button>
                  </div>
                </div>
                <button
                  onClick={dismissInstallPrompt}
                  className="text-stone-400 hover:text-stone-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Update Prompt */}
      <AnimatePresence>
        {updateAvailable && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-4 left-4 right-4 z-50 md:left-auto md:w-96"
          >
            <div className="bg-white rounded-lg shadow-xl border border-stone-200 p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <RefreshCw className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-stone-900">Update Available</h3>
                  <p className="text-sm text-stone-600 mt-1">A new version is ready to install</p>
                  <Button
                    size="sm"
                    onClick={updateApp}
                    className="mt-3 bg-green-600 hover:bg-green-700"
                  >
                    Update Now
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Offline Indicator */}
      <AnimatePresence>
        {!isOnline && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="fixed top-0 left-0 right-0 z-50 bg-yellow-500 text-white text-center py-2 text-sm"
          >
            <div className="flex items-center justify-center gap-2">
              <WifiOff className="w-4 h-4" />
              You're currently offline. Some features may be limited.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// PWA Features Component
export const PWAFeatures: React.FC = () => {
  const {
    isInstallable,
    isInstalled,
    isOnline,
    installApp,
    enableNotifications,
    notificationsEnabled,
    sendNotification,
  } = usePWA();

  const handleTestNotification = () => {
    sendNotification('Test Notification', {
      body: 'This is a test notification from Platform Engineering Nexus',
      icon: '/favicon.ico',
    });
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold text-stone-900">PWA Features</h1>

      <div className="grid gap-6">
        {/* Installation Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Installation</h2>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-stone-700">
                {isInstalled ? 'App is installed' : 'App is not installed'}
              </p>
              <p className="text-sm text-stone-500 mt-1">
                {isInstalled
                  ? 'You can use the app offline and receive notifications'
                  : 'Install the app for a better experience'}
              </p>
            </div>
            {isInstallable && !isInstalled && (
              <Button onClick={installApp}>
                <Download className="w-4 h-4 mr-2" />
                Install App
              </Button>
            )}
          </div>
        </div>

        {/* Network Status */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Network Status</h2>
          <div className="flex items-center gap-3">
            {isOnline ? (
              <>
                <Wifi className="w-5 h-5 text-green-600" />
                <span className="text-green-600 font-medium">Online</span>
                <span className="text-stone-500">All features available</span>
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-red-600" />
                <span className="text-red-600 font-medium">Offline</span>
                <span className="text-stone-500">Limited functionality</span>
              </>
            )}
          </div>
        </div>

        {/* Notifications */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Notifications</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-stone-700">
                  {notificationsEnabled ? 'Notifications enabled' : 'Notifications disabled'}
                </p>
                <p className="text-sm text-stone-500 mt-1">
                  Get notified about important updates and events
                </p>
              </div>
              {!notificationsEnabled && (
                <Button onClick={enableNotifications} variant="outline">
                  Enable
                </Button>
              )}
            </div>

            {notificationsEnabled && (
              <Button onClick={handleTestNotification} variant="outline">
                Send Test Notification
              </Button>
            )}
          </div>
        </div>

        {/* Offline Features */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Offline Features</h2>
          <div className="grid gap-3">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-stone-700">Browse previously visited pages</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-stone-700">View cached content</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full" />
              <span className="text-stone-700">Limited form submissions (queued)</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-stone-700">No real-time data updates</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook for offline storage
export const useOfflineStorage = () => {
  const { isOnline } = usePWA();

  const saveOfflineData = (key: string, data: any) => {
    try {
      localStorage.setItem(
        `offline_${key}`,
        JSON.stringify({
          data,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      console.error('Failed to save offline data:', error);
    }
  };

  const getOfflineData = (key: string) => {
    try {
      const stored = localStorage.getItem(`offline_${key}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed.data;
      }
    } catch (error) {
      console.error('Failed to get offline data:', error);
    }
    return null;
  };

  const clearOfflineData = (key?: string) => {
    if (key) {
      localStorage.removeItem(`offline_${key}`);
    } else {
      // Clear all offline data
      Object.keys(localStorage)
        .filter(k => k.startsWith('offline_'))
        .forEach(k => localStorage.removeItem(k));
    }
  };

  const syncPendingData = async () => {
    if (!isOnline) return;

    try {
      const pendingKeys = Object.keys(localStorage).filter(k => k.startsWith('offline_pending_'));

      for (const key of pendingKeys) {
        const data = getOfflineData(key.replace('offline_', ''));
        if (data) {
          // Attempt to sync with server
          // This would be implemented based on your API structure
          console.log('Syncing pending data:', key, data);
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.error('Failed to sync pending data:', error);
    }
  };

  return {
    saveOfflineData,
    getOfflineData,
    clearOfflineData,
    syncPendingData,
    isOnline,
  };
};

export default PWAProvider;
